import { IamGroupType } from '@navio/nsdk';
import { type FilterOptionSource, filterUnitFor, useFilterSpecValue } from 'noddi-ui-web';
import { useAccountOptions } from '../Account/useAccountFilter';
import { useBrandOptions } from '../Brand/useBrandFilter';
import { CAR_MAKE_FILTER_TEXT, useCarMakeNameOptions } from '../Car/useCarMakeNamesFilter';
import { CAR_MODEL_FILTER_TEXT, useCarModelNameOptions } from '../Car/useCarModelNamesFilter';
import { useGarageOptions } from '../Garage/useGarageFilter';
import { ORGANIZATION_FILTER_TEXT, useOrganizationOptions } from '../Organization/useOrganizationFilter';
import { useSalesItemOptions } from '../SalesItem/useSalesItemsFilter';
import {
  SERVICE_AREA_FILTER_TEXT,
  useServiceAreaOptions
} from '../ServiceDepartment/useServiceDepartmentServiceAreasFilter';
import { useSitePortOptions } from '../SitePort/useSitePortFilter';
import { useUserGroupOptions } from '../UserGroup/useUserGroupFilter';
import { useWorkstationOptions } from '../Workstation/useWorkstationFilter';
import { bookingFilterUnit } from './bookingFilterSet';

/** "Booked on brand", unscoped: a marketplace booking's brand is not its service organization's brand. */
export const useBookedOnBrandFilter = () =>
  bookingFilterUnit.multiSelect('brandIds', {
    title: 'Booked on brand',
    description: 'The brand the customer booked on.',
    source: useBrandOptions({}),
    exclude: true
  });

/**
 * Booking filters whose options load, shared by every booking list; each list picks the ones it shows.
 * `serviceDepartmentIds` scopes the department-bound options; `serviceOrganizationIds` (admin's own filter)
 * narrows the service areas offered.
 */
export const useBookingFilterSpecs = ({
  serviceDepartmentIds,
  serviceOrganizationIds
}: {
  serviceDepartmentIds?: number[];
  serviceOrganizationIds?: number[];
}) => {
  const garage = bookingFilterUnit.multiSelect('garageIds', {
    title: 'Garages',
    description: "Filter by the garage of the booking's workstation.",
    source: useGarageOptions({ serviceDepartmentIds }),
    exclude: true
  });
  // Workstations offered are the picked garages' ones.
  const { garageIds } = useFilterSpecValue(garage);
  const carMake = bookingFilterUnit.multiSelect('makeNames', {
    ...CAR_MAKE_FILTER_TEXT,
    source: useCarMakeNameOptions(),
    exclude: true
  });

  return {
    // Unscoped: a department scope hides organizations with no prior bookings there (e.g. recalls).
    organization: bookingFilterUnit.multiSelect('organizationIds', {
      ...ORGANIZATION_FILTER_TEXT,
      source: useOrganizationOptions(),
      exclude: true
    }),
    groups: bookingFilterUnit.multiSelect('userGroupIds', {
      title: 'Groups',
      description: 'Filter by specific groups',
      defaultVisibleOptions: 3,
      source: useUserGroupOptions({ types: [IamGroupType.GROUP] }),
      exclude: true
    }),
    driver: bookingFilterUnit.multiSelect('carManagerIds', {
      title: 'Driver',
      description: 'Filter by people who are a driver or car manager for the car.',
      source: useAccountOptions(),
      exclude: true
    }),
    brand: useBookedOnBrandFilter(),
    products: bookingProductsFilter({
      primary: useSalesItemOptions({ serviceDepartmentIds, filters: { isPrimary: true } }),
      addon: useSalesItemOptions({ serviceDepartmentIds, filters: { isAddon: true } })
    }),
    serviceAreas: bookingFilterUnit.multiSelect('serviceAreaIds', {
      ...SERVICE_AREA_FILTER_TEXT,
      source: useServiceAreaOptions({ serviceDepartmentIds, serviceOrganizationIds }),
      exclude: true
    }),
    garage,
    sitePort: bookingFilterUnit.multiSelect('sitePortIds', {
      title: 'Building ports',
      description: 'Filter by the building port of the booking.',
      source: useSitePortOptions({ serviceDepartmentIds }),
      exclude: true
    }),
    workstation: bookingFilterUnit.multiSelect('workstationIds', {
      title: 'Workstations',
      description: 'Filter by the workstation of the booking.',
      source: useWorkstationOptions({ garageIds, serviceDepartmentIds }),
      exclude: true
    }),
    carMake,
    // Registry model names are not scoped to a make, so the model options do not narrow with the make picked.
    carModel: bookingFilterUnit.multiSelect('modelNames', {
      ...CAR_MODEL_FILTER_TEXT,
      source: useCarModelNameOptions(),
      exclude: true
    })
  };
};

/** URL-only params of the two product pickers; the bookings endpoint takes both as `salesItemIds`. */
const productPickerUnit = filterUnitFor<{
  primarySalesItemIds?: number[];
  excludedPrimarySalesItemIds?: number[];
  addonSalesItemIds?: number[];
  excludedAddonSalesItemIds?: number[];
}>();

/** Primary and addon products: two pickers sent as the one `salesItemIds` param (and its exclusion). */
const bookingProductsFilter = ({
  primary,
  addon
}: {
  primary: FilterOptionSource<number>;
  addon: FilterOptionSource<number>;
}) =>
  bookingFilterUnit.map(
    [
      productPickerUnit.multiSelect('primarySalesItemIds', {
        title: 'Primary products',
        description: 'Filter by products',
        defaultVisibleOptions: 3,
        source: primary,
        exclude: true
      }),
      productPickerUnit.multiSelect('addonSalesItemIds', {
        title: 'Addon products',
        description: 'Filter by products',
        defaultVisibleOptions: 3,
        source: addon,
        exclude: true
      })
    ],
    (params) => ({
      salesItemIds: [...(params.primarySalesItemIds ?? []), ...(params.addonSalesItemIds ?? [])],
      excludedSalesItemIds: [...(params.excludedPrimarySalesItemIds ?? []), ...(params.excludedAddonSalesItemIds ?? [])]
    })
  );
